import {HttpRequestType} from '../../features/httprequesttracking/HttpRequestType';
import type {OnDownloadFinishedEventObject} from '../../features/httprequesttracking/OnDownloadFinishedEventObject';
import type {DrmPerformanceInfo} from '../../types/DrmPerformanceInfo';
import {isNotNullish} from '../../utils/Utils';

/**
 * Service that subscribes to network download events and tracks
 * DRM license acquisition performance.
 *
 * Only the first successful DRM license request is tracked,
 * capturing the DRM type and load time.
 */
export class DrmPerformanceService {
  private drmPerformanceInfo: DrmPerformanceInfo | undefined = undefined;

  /**
   * Returns the captured DRM performance information.
   * @returns DrmPerformanceInfo if a license was tracked, undefined otherwise
   */
  getInfo(): DrmPerformanceInfo | undefined {
    return this.drmPerformanceInfo;
  }

  /**
   * Resets the service state for a new playback session.
   * Clears tracked DRM info and allows tracking of the next license request.
   */
  reset(): void {
    this.drmPerformanceInfo = undefined;
  }

  onDownloadFinished = (event: OnDownloadFinishedEventObject): void => {
    const httpRequest = event.httpRequest;

    // Only track successful requests
    if (!httpRequest.success) {
      return;
    }

    // Only track the first DRM license request (not certificates)
    if (isNotNullish(this.drmPerformanceInfo) || !isDrmLicenseType(httpRequest.type)) {
      return;
    }

    const drmType = extractDrmType(httpRequest.type);
    this.drmPerformanceInfo = {
      drmType: drmType,
      drmLoadTime: httpRequest.downloadTime,
    };
  };
}

export function isDrmLicenseType(type: HttpRequestType): boolean {
  return (
    type === HttpRequestType.DRM_LICENSE_WIDEVINE ||
    type === HttpRequestType.DRM_LICENSE_FAIRPLAY ||
    type === HttpRequestType.DRM_LICENSE_PLAYREADY ||
    type === HttpRequestType.DRM_LICENSE_CLEARKEY
  );
}

export function extractDrmType(requestType: HttpRequestType): string {
  // Extract 'widevine' from 'drm/license/widevine'
  const match = requestType.match(/drm\/license\/(.+)/);
  return match ? match[1] : 'unknown';
}
